import React, { type ReactNode, useMemo } from 'react';
import classNames from 'classnames';
import { type NftTraits, generateTraits, RARITY_COLORS } from '../../utils/rarity';
import styles from './styles.module.css';

export type EsimCardColor = 'olive' | 'teal' | 'blue' | 'purple' | 'orange' | 'pink' | 'gray' | 'green' | 'red';

/**
 * Type of SVG to load from backend
 * - 'full': Complete eSIM (background + pattern + chip)
 * - 'body': Chip only (base + inlay + symbol)
 * - 'background': Background only (gradient + pattern)
 * - 'none': No SVG loading, use CSS gradient preview
 */
export type SvgLoadType = 'full' | 'body' | 'background' | 'none';

const colorStyles: Record<EsimCardColor, string> = {
	olive: styles['card--olive'],
	teal: styles['card--teal'],
	blue: styles['card--blue'],
	purple: styles['card--purple'],
	orange: styles['card--orange'],
	pink: styles['card--pink'],
	gray: styles['card--gray'],
	green: styles['card--green'],
	red: styles['card--red'],
};

/**
 * Get the base API URL
 */
function getApiBaseUrl(): string {
	return (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '/api';
}

/**
 * Constructs SVG URL for an NFT based on type
 * @param tokenId - Token ID (phone number without +)
 * @param type - Type of SVG to load
 * @returns Full URL to the NFT SVG
 */
export function getNftSvgUrl(tokenId: string, type: SvgLoadType): string | null {
	if (type === 'none') return null;
	const baseUrl = getApiBaseUrl();
	switch (type) {
		case 'full':
			return `${baseUrl}/utility/nft/${tokenId}/image.svg`;
		case 'body':
			return `${baseUrl}/utility/nft/${tokenId}/body.svg`;
		case 'background':
			return `${baseUrl}/utility/nft/${tokenId}/background.svg`;
		default:
			return null;
	}
}

/**
 * @deprecated Use getNftSvgUrl instead
 */
export function getNftImageUrl(tokenId: string): string {
	return getNftSvgUrl(tokenId, 'full')!;
}

export interface EsimCardBackgroundProps {
	/**
	 * Background color variant (used when no SVG/traits provided)
	 */
	color?: EsimCardColor;

	/**
	 * URL to SVG image from metadata API
	 * Example: https://api.tonesim.app/api/utility/nft/{tokenId}/image.svg
	 */
	svgUrl?: string;

	/**
	 * Phone number to generate traits from
	 * Used for gradient preview and can auto-generate svgUrl
	 */
	phoneNumber?: string;

	/**
	 * Pre-computed traits (alternative to phoneNumber)
	 */
	traits?: NftTraits;

	/**
	 * Type of SVG to load from backend
	 * - 'full': Complete eSIM (background + pattern + chip)
	 * - 'body': Chip only (base + inlay + symbol)
	 * - 'background': Background only (gradient + pattern)
	 * - 'none': No SVG loading, use CSS gradient preview
	 * @default 'none'
	 */
	svgType?: SvgLoadType;

	/**
	 * @deprecated Use svgType='full' instead
	 * Load full SVG from API instead of just gradient preview
	 * Requires phoneNumber to be set
	 * @default false
	 */
	loadFullSvg?: boolean;

	/**
	 * Additional CSS class name
	 */
	className?: string;

	/**
	 * Click handler
	 */
	onClick?: () => void;

	/**
	 * Content to display inside the card
	 */
	children: ReactNode;
}

/**
 * EsimCardBackground component - displays the card background based on NFT metadata
 *
 * Features:
 * - Radial gradient background matching backend SVG design
 * - Full SVG loading from API (optional)
 * - Rarity-based visual effects (glow for epic/legendary)
 * - Fallback color variants
 * - Click handling with keyboard support
 * - Accessibility support
 *
 * Usage:
 * 1. Quick preview (gradient only): <EsimCardBackground phoneNumber="+371..." />
 * 2. Full SVG from API: <EsimCardBackground phoneNumber="+371..." loadFullSvg />
 * 3. Manual SVG URL: <EsimCardBackground svgUrl="..." />
 * 4. Fallback color: <EsimCardBackground color="blue" />
 */
export function EsimCardBackground({
	color = 'olive',
	svgUrl,
	phoneNumber,
	traits: propTraits,
	svgType = 'none',
	loadFullSvg = false,
	className,
	onClick,
	children,
}: EsimCardBackgroundProps) {
	const normalizedPhone = useMemo(() => {
		if (!phoneNumber) return null;
		return phoneNumber.replace(/\s+/g, '').replace('+', '');
	}, [phoneNumber]);

	const traits = useMemo(() => {
		if (propTraits) return propTraits;
		if (normalizedPhone) {
			return generateTraits(normalizedPhone);
		}
		return null;
	}, [propTraits, normalizedPhone]);

	const effectiveSvgType = useMemo((): SvgLoadType => {
		if (svgType !== 'none') return svgType;
		if (loadFullSvg) return 'full';
		return 'none';
	}, [svgType, loadFullSvg]);

	const effectiveSvgUrl = useMemo(() => {
		if (svgUrl) return svgUrl;
		if (effectiveSvgType !== 'none' && normalizedPhone) {
			return getNftSvgUrl(normalizedPhone, effectiveSvgType);
		}
		return null;
	}, [svgUrl, effectiveSvgType, normalizedPhone]);

	const traitStyles = useMemo(() => {
		if (!traits || effectiveSvgUrl) return {};

		const { from, to } = traits.backgroundGradient;
		const rarityColor = RARITY_COLORS[traits.inlayRarity];
		const hasGlow = traits.inlayRarity === 'epic' || traits.inlayRarity === 'legendary';

		return {
			background: `radial-gradient(ellipse 71% 71% at 50% 50%, ${from} 11%, ${to} 100%)`,
			...(hasGlow && {
				boxShadow: `inset 0 0 30px ${rarityColor.bg}, ${rarityColor.glow || 'none'}`,
			}),
		};
	}, [traits, effectiveSvgUrl]);

	const svgStyles = useMemo(() => {
		if (!effectiveSvgUrl) return {};
		return {
			backgroundImage: `url(${effectiveSvgUrl})`,
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
		};
	}, [effectiveSvgUrl]);

	const useSvgStyles = !!effectiveSvgUrl;
	const useTraitStyles = !useSvgStyles && traits;
	const useColorClass = !useTraitStyles && !useSvgStyles;

	return (
		<div
			className={classNames(
				styles.root,
				useColorClass && colorStyles[color],
				useTraitStyles && styles['card--traits'],
				useSvgStyles && styles['card--svg'],
				className
			)}
			style={{
				...(useTraitStyles ? traitStyles : {}),
				...(useSvgStyles ? svgStyles : {}),
			}}
			onClick={onClick}
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
			onKeyDown={onClick ? (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onClick();
				}
			} : undefined}
			data-rarity={traits?.inlayRarity}
			data-background={traits?.background}
			data-inlay={traits?.inlay}
		>
			{children}
		</div>
	);
}
